import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaArrowLeft, FaUser, FaMoneyBillWave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getBrandsByCompany } from '../../services/companyAuthService';
import { fetchCampaigns } from '../../../Redux/campaignSlice';
import { getCampaignAffiliateFinancials } from '../../../influencer/services/affiliateService';
import PaymentDialog from './PaymentDialog';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

 const FinancialTransaction = () => {
  const dispatch = useDispatch();
  const { campaigns, loading } = useSelector((state) => state.campaign);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [financialData, setFinancialData] = useState([]);
  const [timeRange, setTimeRange] = useState('7d'); // 7d, 30d, 90d, all
  const [activeTab, setActiveTab] = useState('financial'); // affiliate-links, youtube, instagram, financial
  const [error, setError] = useState(null);
  const [loadingFinancial, setLoadingFinancial] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);

  // Reset campaign when brand changes
  useEffect(() => {
    setSelectedCampaign('');
    setFinancialData([]);
  }, [selectedBrand]);

  const fetchBrands = useCallback(async () => {
    try {
      setLoadingBrands(true);
      const response = await getBrandsByCompany();
      if (response.success) {
        setBrands(response.brands);
      } else {
        toast.error(response.message || 'Failed to fetch brands');
      }
    } catch (err) {
      toast.error('Failed to fetch brands');
      setError(err.message);
    } finally {
      setLoadingBrands(false);
    }
  }, []);
  
  useEffect(() => {
    fetchBrands();
    dispatch(fetchCampaigns());
  }, [fetchBrands, dispatch]);
  
  const fetchFinancialData = useCallback(async () => {
    try {
      if (!selectedCampaign) return;
  
      setLoadingFinancial(true);
      const response = await getCampaignAffiliateFinancials(selectedCampaign);
  
      if (response.success) {
        setFinancialData(response.data || []);
      } else {
        toast.error(response.message || 'Failed to fetch financial data');
        setFinancialData([]);
      }
    } catch (error) {
      toast.error('Failed to fetch financial data');
      setFinancialData([]);
    } finally {
      setLoadingFinancial(false);
    }
  }, [selectedCampaign]);
  
  useEffect(() => {
    setFinancialData([]);
    if (selectedCampaign) {
      fetchFinancialData();
    }
  }, [selectedCampaign, timeRange, fetchFinancialData]);

  // const handlePayNow = (campaignId, affiliateId) => {
  //   navigate(`/company/dashboard/campaigns/${campaignId}/affiliate/${affiliateId}/payment`);
  // }

  const handlePayNow = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setPaymentDialogOpen(true);
  };

  const handleDialogClose = (paymentSuccess) => {
    setPaymentDialogOpen(false);
    setSelectedAffiliate(null);
    
    // Refresh financial data if payment was successful
    if (paymentSuccess) {
      fetchFinancialData();
    }
  };

  const filteredCampaigns = useMemo(() => {
    if (!selectedBrand) return [];
    return campaigns.filter(
      c => (c.brandId?._id || c.brandId) === selectedBrand
    );
  }, [campaigns, selectedBrand]);

  if (loading || loadingBrands) {
    return (
      <div className="flex justify-center items-center min-h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-full">
        <div className="text-red-500 text-center">
          <h2 className="text-xl font-bold mb-2">Error Loading Data</h2>
          <p>{error}</p>
          <button
            onClick={() => {
              setError(null);
              fetchBrands();
              dispatch(fetchCampaigns());
            }}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className=" min-h-full mt-6">
      <div className="container mx-auto p-4 md:p-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div className="flex items-center mb-4 md:mb-0">
            <button
              onClick={() => window.history.back()}
              className="text-gray-600 p-2 rounded-full hover:bg-gray-200 lg:mr-4"
            >
              <FaArrowLeft size={22} />
            </button>
            <h2 className="lg:text-2xl text-xl font-bold text-gray-800">
              Financial Transactions
            </h2>
          </div>

          {/* Time Range Selector */}
          <div className="flex space-x-2">
            {['7d', '30d', '90d', 'all'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1 rounded-full text-sm ${timeRange === range
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
              >
                {range === 'all' ? 'All Time' : range}
              </button>
            ))}
          </div>
        </div>

        {/* Brand & Campaign Selection */}
        <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
          <div className="mb-4">
            <label htmlFor="brand" className="block text-sm font-medium text-gray-700 mb-2">
              Select Brand
            </label>
            <select
              id="brand"
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Select a brand</option>
              {brands.map((brand) => (
                <option key={brand._id} value={brand._id}>
                  {brand.brandName}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-2">
            <label htmlFor="campaign" className="block text-sm font-medium text-gray-700 mb-2">
              Select Campaign
            </label>
            <select
              id="campaign"
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
              disabled={!selectedBrand}
            >
              <option value="">{selectedBrand ? 'Select a campaign' : 'Select a brand first'}</option>
              {filteredCampaigns.map(c => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
            </select>
          </div>
        </div>
        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav 
              className="flex overflow-x-auto px-4 sm:px-6" 
              style={{
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                WebkitScrollbar: { display: 'none' }
              }} 
              aria-label="Tabs"
            >
              <div className="flex space-x-4 sm:space-x-8 min-w-max">
                <button
                  onClick={() => setActiveTab('financial')}
                  className={`py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${activeTab === 'financial'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <FaMoneyBillWave className="inline mr-1 sm:mr-2 text-xs sm:text-sm" />
                  <span className="hidden sm:inline">Financial</span>
                  <span className="sm:hidden">Finance</span>
                </button>
              </div>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            {activeTab === 'financial' && (
              <>
                <h4 className="text-lg font-semibold mb-4 flex items-center">
                  <FaMoneyBillWave className="text-orange-500 mr-2" />
                  Financial Performance
                </h4>
                {loadingFinancial ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                    <span className="ml-2 text-gray-600">Loading financial data...</span>
                  </div>
                ) : financialData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FaMoneyBillWave className="mx-auto text-4xl mb-4 text-gray-300" />
                    <p>No financial data available for this campaign</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Influencer
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Due
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount Paid
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount Unpaid
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Currency
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Updated
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {financialData.map((affiliate) => {
                          try {
                            return (
                          <tr key={affiliate._id} className="hover:bg-gray-50">
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                                  <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-orange-100 flex items-center justify-center">
                                    <FaUser className="text-orange-500 text-xs sm:text-sm" />
                                  </div>
                                </div>
                                <div className="ml-2 sm:ml-4 min-w-0">
                                  <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                                    {(affiliate.firstName && affiliate.firstName !== 'Unknown' &&
                                      affiliate.lastName && affiliate.lastName !== 'User')
                                      ? `${affiliate.firstName} ${affiliate.lastName}`
                                      : affiliate.username || 'Unknown User'}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate">
                                    {affiliate.email}
                                  </div>
                                  {affiliate.instagramId && affiliate.instagramId !== 'N/A' && (
                                    <div className="text-xs text-gray-400 truncate">
                                      @{affiliate.instagramId}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm font-medium text-gray-900">
                                {affiliate.financial?.currency?.symbol || '$'}{(affiliate.financial?.totalDue || 0).toLocaleString()}
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-green-600 font-medium">
                                {affiliate.financial?.currency?.symbol || '$'}{(affiliate.financial?.amountPaid || 0).toLocaleString()}
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-red-600 font-medium">
                                {affiliate.financial?.currency?.symbol || '$'}{(affiliate.financial?.amountUnpaid || 0).toLocaleString()}
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-900">
                                {typeof affiliate.financial?.currency === 'object' 
                                  ? affiliate.financial?.currency?.code || 'USD'
                                  : affiliate.financial?.currency || 'USD'
                                }
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-500">
                                {affiliate.financial?.lastUpdated 
                                  ? new Date(affiliate.financial.lastUpdated).toLocaleDateString()
                                  : 'N/A'
                                }
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-500">
                                <button 
                                // onClick={() => handlePayNow(selectedCampaign, affiliate._id)}
                                  onClick={() => handlePayNow(affiliate)}
                                  className="bg-orange-500 text-white px-2 py-1 rounded-md text-xs sm:text-sm hover:bg-orange-600 transition-colors"
                                >
                                  Pay Now
                                </button>
                              </div>
                            </td>
                          </tr>
                            );
                          } catch (error) {
                            console.error('Error rendering affiliate row:', error, affiliate);
                            return null;
                          }
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Payment Dialog */}
      <PaymentDialog
        isOpen={paymentDialogOpen}
        onClose={handleDialogClose}
        campaignId={selectedCampaign}
        affiliateId={selectedAffiliate?._id}
        affiliateData={selectedAffiliate}
      />
    </div>
  );
};

export default FinancialTransaction; 